import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useAuth } from '@structural-climbing/auth'
import {
  Button,
  Input,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  Select,
  SelectItem,
  Spinner,
  DateField,
} from '@structural-climbing/ui'
import { useTrainers } from './useTrainers'
import { useTrainerAssignments, type TrainerAssignment } from './useTrainerAssignments'
import { ExtendAssignmentModal } from './ExtendAssignmentModal'
import { ReassignTrainerModal } from './ReassignTrainerModal'

interface Child {
  id: string
  first_name: string
  last_name: string | null
}

interface Props {
  memberUserId: string
  memberEmail: string
  memberName: string
  /**
   * Opened from a child's own profile: the assignment is for this child only,
   * so the who-picker is hidden and the current list shows just them.
   */
  childId?: string | null
  onClose: () => void
  onAssigned: () => void
}

/** The adult themselves, as a value the child Select can hold. */
const SELF = '__self__'

export function AssignTrainerModal({
  memberUserId,
  memberEmail,
  memberName,
  childId = null,
  onClose,
  onAssigned,
}: Props) {
  const { t } = useTranslation()
  const { getAccessTokenSilently } = useAuth()
  const apiUrl = import.meta.env.VITE_API_URL as string
  const { trainers, loading: trainersLoading } = useTrainers()
  // Opened from the members row menu there is no other view of who already
  // coaches this person, so the current assignments belong here — otherwise the
  // only way to find out is to assign a duplicate and read the 409.
  const { assignments, reload: reloadAssignments } = useTrainerAssignments(memberUserId)
  const [removingId, setRemovingId] = useState<string | null>(null)
  // The same three actions the member profile offers. Two entry points that can
  // do different things to the same row is a trap: whichever one you happen to
  // open should be able to finish the job.
  const [extendTarget, setExtendTarget] = useState<TrainerAssignment | null>(null)
  const [reassignTarget, setReassignTarget] = useState<TrainerAssignment | null>(null)

  const [children, setChildren] = useState<Child[]>([])
  const [trainerId, setTrainerId] = useState('')
  // Several at once: a parent and their kids often start with the same coach,
  // and each still gets a row of their own — an assignment to a child is never
  // an assignment to the parent.
  const [who, setWho] = useState<string[]>([childId ?? SELF])
  const [startsAt, setStartsAt] = useState('')
  const [endsAt, setEndsAt] = useState('')
  const [note, setNote] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Children are offered in the same modal so the front desk assigns a kid to a
  // coach in one step rather than hunting for a separate screen.
  useEffect(() => {
    let cancelled = false
    void (async () => {
      try {
        const token = await getAccessTokenSilently()
        const resp = await fetch(
          `${apiUrl}/members/children?member_email=${encodeURIComponent(memberEmail)}&member_user_id=${encodeURIComponent(memberUserId)}`,
          { headers: { Authorization: `Bearer ${token}` } },
        )
        if (!resp.ok) return
        const data = (await resp.json()) as { children: Child[] }
        if (!cancelled) setChildren(data.children ?? [])
      } catch {
        // A member with no children is the common case; failing quietly just
        // means the picker offers the adult only.
      }
    })()
    return () => {
      cancelled = true
    }
  }, [apiUrl, getAccessTokenSilently, memberEmail, memberUserId])

  const live = assignments.filter(
    (a) => a.status !== 'past' && (!childId || a.child_id === childId),
  )
  // A trainer is never their own trainer. They may still coach their own
  // children, so they drop out of the list only while the adult is picked.
  const trainerOptions = who.includes(SELF)
    ? trainers.filter((tr) => tr.user_id !== memberUserId)
    : trainers
  const pickedTrainer = trainerOptions.some((tr) => tr.user_id === trainerId) ? trainerId : ''

  function childName(child: Child) {
    return [child.first_name, child.last_name].filter(Boolean).join(' ')
  }

  async function handleRemove(id: string) {
    setRemovingId(id)
    try {
      const token = await getAccessTokenSilently()
      await fetch(`${apiUrl}/trainer-assignments/${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` },
      })
      await reloadAssignments()
      onAssigned()
    } finally {
      setRemovingId(null)
    }
  }

  function whoName(key: string) {
    if (key === SELF) return memberName
    const child = children.find((c) => c.id === key)
    return child ? childName(child) : memberName
  }

  async function handleSubmit() {
    if (!pickedTrainer || who.length === 0) return
    setSaving(true)
    setError(null)
    try {
      const token = await getAccessTokenSilently()
      // One request per person, in turn. Empty dates make an open-ended
      // assignment that starts today.
      const failures: string[] = []
      let created = 0
      for (const key of who) {
        const resp = await fetch(`${apiUrl}/trainer-assignments`, {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify({
            trainer_user_id: pickedTrainer,
            member_user_id: memberUserId,
            child_id: key === SELF ? null : key,
            starts_at: startsAt || null,
            ends_at: endsAt || null,
            note: note.trim() || null,
          }),
        })
        if (resp.ok) {
          created += 1
          continue
        }
        const { error: msg } = (await resp.json().catch(() => ({}))) as { error?: string }
        const reason = msg ?? t('trainers.errorAssign')
        // Name the person only when there is more than one to tell apart.
        failures.push(who.length > 1 ? `${whoName(key)}: ${reason}` : reason)
      }
      if (created > 0) {
        await reloadAssignments()
        onAssigned()
      }
      if (failures.length > 0) {
        // Keep whoever failed selected so a second press retries only them.
        setError(failures.join(' '))
        return
      }
      onClose()
    } catch (e) {
      setError(e instanceof Error ? e.message : t('trainers.errorAssign'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <>
      {extendTarget && (
        <ExtendAssignmentModal
          assignment={extendTarget}
          onClose={() => setExtendTarget(null)}
          onSaved={() => {
            void reloadAssignments()
            onAssigned()
          }}
        />
      )}
      {reassignTarget && (
        <ReassignTrainerModal
          assignment={reassignTarget}
          onClose={() => setReassignTarget(null)}
          onReassigned={() => {
            void reloadAssignments()
            onAssigned()
          }}
        />
      )}
      <Modal scrollBehavior="inside" isOpen onOpenChange={(open) => !open && onClose()}>
        <ModalContent>
          {() => (
            <>
              <ModalHeader className="flex-col items-start gap-1">
                <span>{t('trainers.assignTitle')}</span>
                <span className="text-sm font-normal text-foreground/60">{memberName}</span>
              </ModalHeader>
              <ModalBody>
                {trainersLoading ? (
                  <div className="flex justify-center py-6">
                    <Spinner size="sm" color="primary" />
                  </div>
                ) : trainers.length === 0 ? (
                  <p className="py-4 text-sm text-foreground/60">{t('trainers.noTrainers')}</p>
                ) : (
                  <div className="flex flex-col gap-4">
                    {live.length > 0 && (
                      <div className="flex flex-col gap-1.5">
                        <p className="text-xs font-semibold uppercase tracking-wide text-foreground/40">
                          {t('trainers.currentlyAssigned')}
                        </p>
                        {live.map((a) => (
                          <div
                            key={a.id}
                            className="flex items-center justify-between gap-2 rounded-lg bg-content2/50 px-3 py-2"
                          >
                            <div className="min-w-0">
                              <p className="truncate text-sm">
                                {a.trainer_name ?? t('trainers.unknownTrainer')}
                                {!childId && a.child_name && (
                                  <span className="ml-2 rounded-full bg-primary/10 px-1.5 py-0.5 text-xs text-primary">
                                    {a.child_name}
                                  </span>
                                )}
                              </p>
                              {a.status === 'upcoming' && (
                                <p className="text-xs text-foreground/40">
                                  {t('trainers.startsOn', { date: a.starts_at })}
                                </p>
                              )}
                            </div>
                            <div className="flex shrink-0 items-center gap-1">
                              <Button size="sm" variant="light" onClick={() => setExtendTarget(a)}>
                                {t('trainers.extendAction')}
                              </Button>
                              <Button
                                size="sm"
                                variant="light"
                                onClick={() => setReassignTarget(a)}
                              >
                                {t('trainers.reassignAction')}
                              </Button>
                              <Button
                                size="sm"
                                isIconOnly
                                variant="light"
                                color="danger"
                                aria-label={t('trainers.removeAssignment')}
                                isDisabled={removingId === a.id}
                                onClick={() => void handleRemove(a.id)}
                              >
                                <svg
                                  width="16"
                                  height="16"
                                  viewBox="0 0 24 24"
                                  fill="none"
                                  stroke="currentColor"
                                  strokeWidth="2"
                                  strokeLinecap="round"
                                >
                                  <path d="M3 6h18M8 6V4h8v2M19 6l-1 14H6L5 6M10 11v6M14 11v6" />
                                </svg>
                              </Button>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}

                    <Select
                      label={t('trainers.trainerLabel')}
                      selectedKeys={pickedTrainer ? [pickedTrainer] : []}
                      onSelectionChange={(keys) =>
                        setTrainerId((Array.from(keys)[0] as string) ?? '')
                      }
                    >
                      {trainerOptions.map((trainer) => (
                        <SelectItem key={trainer.user_id} textValue={trainer.name}>
                          {trainer.name}
                        </SelectItem>
                      ))}
                    </Select>

                    {!childId && children.length > 0 && (
                      <Select
                        label={t('trainers.whoLabel')}
                        description={t('trainers.whoHint')}
                        selectionMode="multiple"
                        selectedKeys={who}
                        onSelectionChange={(keys) => setWho(Array.from(keys) as string[])}
                      >
                        {[
                          <SelectItem key={SELF} textValue={memberName}>
                            {memberName}
                          </SelectItem>,
                          ...children.map((child) => (
                            <SelectItem key={child.id} textValue={childName(child)}>
                              {childName(child)}
                            </SelectItem>
                          )),
                        ]}
                      </Select>
                    )}

                    {/* Left empty, the assignment starts today and has no end. */}
                    <div className="flex flex-col gap-1.5">
                      <div className="grid gap-3 sm:grid-cols-2">
                        <DateField
                          label={t('trainers.startsAtLabel')}
                          value={startsAt}
                          onValueChange={setStartsAt}
                        />
                        <DateField
                          label={t('trainers.endsAtLabel')}
                          value={endsAt}
                          onValueChange={setEndsAt}
                        />
                      </div>
                      <p className="text-xs text-foreground/50">{t('trainers.datesHint')}</p>
                    </div>

                    <Input label={t('trainers.noteLabel')} value={note} onValueChange={setNote} />

                    {error && <p className="text-sm text-danger">{error}</p>}
                  </div>
                )}
              </ModalBody>
              <ModalFooter>
                <Button variant="flat" onClick={onClose} isDisabled={saving}>
                  {t('common.cancel')}
                </Button>
                <Button
                  color="primary"
                  onClick={handleSubmit}
                  isLoading={saving}
                  isDisabled={!pickedTrainer || who.length === 0 || trainers.length === 0}
                >
                  {t('trainers.assignAction')}
                </Button>
              </ModalFooter>
            </>
          )}
        </ModalContent>
      </Modal>
    </>
  )
}
